import { uniqBy } from 'es-toolkit';
import { useCallback } from 'react';
import { useLocation } from 'react-router';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui';
import { useAuth } from '@/context/appContext';
import { PresenceStateUser, useSocketIOPresenceSelect } from '@/socketio/SocketIOService';
import { cmpCustomFoodQueryParams, parseQueryParams } from '@/utils';
import { parseQueueItemRegion } from '@/utils/queueItemRoute';

import Profile from './Profile';

const sameViewerUids = (a: PresenceStateUser[], b: PresenceStateUser[]) => {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i].uid !== b[i].uid) {
      return false;
    }
  }
  return true;
};

const UsersOnCurrentPage = () => {
  const location = useLocation();
  const usersOnCurrentPage = useUsersOnPage(location.pathname + location.search);

  if (!usersOnCurrentPage.length) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        {usersOnCurrentPage.map(u => (
          <Tooltip key={u.uid}>
            <TooltipTrigger asChild>
              <span className="flex h-8 min-w-8 items-center justify-center rounded-full bg-brand-subtle px-2 text-xs font-medium leading-none tabular-nums text-brand">
                {u.uid}
              </span>
            </TooltipTrigger>
            <TooltipContent>{u.uid} is also viewing this page</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
};

const HeaderContent = () => {
  return (
    // Phones drop the empty presence slot and the spacer so their gaps don't push the profile off-screen.
    <div className="flex flex-1 items-center justify-end gap-2">
      <div className="ml-1 shrink-0 empty:hidden sm:empty:block">
        <UsersOnCurrentPage />
      </div>
      <div className="ml-1 hidden w-full sm:block" />
      <Profile />
    </div>
  );
};

const filterUsersOnFood = (
  allActiveUsers: PresenceStateUser[],
  opts: {
    url?: string;
    matchParams?: { [key: string]: string };
  },
  reviewerId: number | undefined,
) => {
  const { matchParams } = opts;
  if (!matchParams) {
    return [] as PresenceStateUser[];
  }

  return uniqBy(
    allActiveUsers
      .filter(u => {
        const uLoc = u.ident?.location;
        if (!uLoc) {
          return false;
        }

        const [, uLocParam] = uLoc.split('?');
        const parsedParams = parseQueryParams(uLocParam);

        for (const param in matchParams) {
          if (parsedParams[param] == matchParams[param]) {
            return true;
          }
        }

        return false;
      })
      .filter(u => u.uid != reviewerId),
    u => u.uid,
  );
};

const filterUsersOnPage = (allActiveUsers: PresenceStateUser[], url: string, reviewerId: number | undefined) => {
  const [urlPath, urlParams] = url.split('?');

  return uniqBy(
    allActiveUsers
      .filter(u => {
        const uLoc = u.ident?.location;
        if (!uLoc) {
          return false;
        }
        const [uLocPath, uLocParam] = uLoc.split('?');

        if (urlPath === '/foods/new') {
          return cmpCustomFoodQueryParams(urlParams, uLocParam);
        }

        // A Canada and a US page can share a path with the same numeric id; keep their viewers apart.
        // Normalize with the same parser the queue-item route uses: a missing region is
        // Canada, and `region=US` and `region=us` both mean US.
        const urlRegion = parseQueueItemRegion(new URLSearchParams(urlParams || ''));
        const uLocRegion = parseQueueItemRegion(new URLSearchParams(uLocParam || ''));
        return uLocPath === urlPath && uLocRegion === urlRegion;
      })
      .filter(u => u.uid != reviewerId),
    u => u.uid,
  );
};

export const useUsersOnFood = (opts: { url?: string; matchParams?: { [key: string]: string } }) => {
  const { authInfo } = useAuth();
  const reviewerId = authInfo?.reviewer_id;
  const url = opts.url || '';
  // Primitive deps — callers often pass a fresh matchParams object each render.
  const triageFoodName = opts.matchParams?.['triage-food-name'];
  const scrapeFoodName = opts.matchParams?.['scrape-food-name'];

  const select = useCallback(
    (state: { allActiveUsers: PresenceStateUser[] }) => {
      const matchParams = {
        ...(triageFoodName != null ? { 'triage-food-name': triageFoodName } : {}),
        ...(scrapeFoodName != null ? { 'scrape-food-name': scrapeFoodName } : {}),
      };

      const usersOnPage = filterUsersOnPage(state.allActiveUsers, url, reviewerId);
      const usersOnFood = filterUsersOnFood(
        state.allActiveUsers,
        { url, matchParams: Object.keys(matchParams).length ? matchParams : undefined },
        reviewerId,
      );
      // Preserve prior behavior: page viewers first, then food-param matches.
      return [...usersOnPage, ...usersOnFood];
    },
    [url, triageFoodName, scrapeFoodName, reviewerId],
  );

  return useSocketIOPresenceSelect(select, sameViewerUids);
};

/**
 * One presence index for the whole foods table: food_name → viewer uid labels.
 * Prefer this over {@link useUsersOnFood} per row — that re-ran lodash filters
 * for every visible food on every presence broadcast.
 */
export type FoodPresenceViewerIndex = {
  /** triage/scrape query-param matches, keyed by food_name */
  byFoodName: Record<string, string[]>;
  /** editor/page path matches, keyed by pathname + region */
  byPathKey: Record<string, string[]>;
};

const sameFoodPresenceIndex = (a: FoodPresenceViewerIndex, b: FoodPresenceViewerIndex) => {
  if (a === b) {
    return true;
  }
  const aNames = Object.keys(a.byFoodName);
  const bNames = Object.keys(b.byFoodName);
  if (aNames.length !== bNames.length) {
    return false;
  }
  for (const name of aNames) {
    const au = a.byFoodName[name];
    const bu = b.byFoodName[name];
    if (!bu || au.length !== bu.length || au.some((uid, i) => uid !== bu[i])) {
      return false;
    }
  }
  const aPaths = Object.keys(a.byPathKey);
  const bPaths = Object.keys(b.byPathKey);
  if (aPaths.length !== bPaths.length) {
    return false;
  }
  for (const path of aPaths) {
    const au = a.byPathKey[path];
    const bu = b.byPathKey[path];
    if (!bu || au.length !== bu.length || au.some((uid, i) => uid !== bu[i])) {
      return false;
    }
  }
  return true;
};

/** Same matching as {@link filterUsersOnPage}: custom foods match by `initial-name`, others by path + region. */
const foodPresencePathKey = (path: string, paramStr: string): string | null => {
  if (path === '/foods/new') {
    const initialName = parseQueryParams(paramStr)['initial-name'];
    return initialName ? `${path}\0${initialName}` : null;
  }
  return `${path}\0${parseQueueItemRegion(new URLSearchParams(paramStr))}`;
};

const pushUniqueUid = (bucket: Record<string, string[]>, key: string | null, uid: string) => {
  if (!key) {
    return;
  }
  bucket[key] ??= [];
  if (!bucket[key].includes(uid)) {
    bucket[key].push(uid);
  }
};

export const useFoodPresenceViewerIndex = (): FoodPresenceViewerIndex => {
  const { authInfo } = useAuth();
  const reviewerId = authInfo?.reviewer_id;

  const select = useCallback(
    (state: { allActiveUsers: PresenceStateUser[] }) => {
      const byFoodName: Record<string, string[]> = {};
      const byPathKey: Record<string, string[]> = {};

      for (const u of state.allActiveUsers) {
        if (!u.uid || String(u.uid) === String(reviewerId)) {
          continue;
        }
        const loc = u.ident?.location as string | undefined;
        if (!loc) {
          continue;
        }
        const [path, paramStr = ''] = loc.split('?');
        const params = parseQueryParams(paramStr);
        const triage = params['triage-food-name'];
        const scrape = params['scrape-food-name'];
        if (triage) {
          pushUniqueUid(byFoodName, triage, String(u.uid));
        }
        if (scrape) {
          pushUniqueUid(byFoodName, scrape, String(u.uid));
        }
        pushUniqueUid(byPathKey, foodPresencePathKey(path, paramStr), String(u.uid));
      }

      return { byFoodName, byPathKey };
    },
    [reviewerId],
  );

  return useSocketIOPresenceSelect(select, sameFoodPresenceIndex);
};

/** Lookup helpers for a row in the foods database table. */
export const foodPresenceViewerUids = (
  index: FoodPresenceViewerIndex,
  opts: { foodName: string; rowUrl: string },
): string[] => {
  const fromName = index.byFoodName[opts.foodName] || [];
  const [path, paramStr = ''] = opts.rowUrl.split('?');
  const pathKey = foodPresencePathKey(path, paramStr);
  const fromPath = (pathKey && index.byPathKey[pathKey]) || [];
  if (!fromName.length) {
    return fromPath;
  }
  if (!fromPath.length) {
    return fromName;
  }
  const seen = new Set(fromName);
  const merged = fromName.slice();
  for (const uid of fromPath) {
    if (!seen.has(uid)) {
      merged.push(uid);
    }
  }
  return merged;
};

export const useUsersOnPage = (url: string) => {
  const { authInfo } = useAuth();
  const reviewerId = authInfo?.reviewer_id;

  const select = useCallback(
    (state: { allActiveUsers: PresenceStateUser[] }) => filterUsersOnPage(state.allActiveUsers, url, reviewerId),
    [url, reviewerId],
  );

  return useSocketIOPresenceSelect(select, sameViewerUids);
};

export default HeaderContent;
